import { TextField, TextFieldProps } from "@mui/material";
import { FC, useState } from "react";

interface PhoneFormProps
  extends Omit<TextFieldProps, "onChange" | "value" | "defaultValue"> {
  onChange?: (phone: string | undefined) => void;
  isValid?: (isValid: boolean) => void;
  defaultValue?: string;
}

/**
 * Passes a value that matches phone regex, otherwise returns undefined.
 * PLEASE NOTE THAT THIS ONLY SUPPORTS AMERICAN NUMBERS (+1)
 * @param onChange passes new value (or undefined) as a property
 * @param isValid returns true if input is valid, otherwise false
 * @param defaultValue sets the initial value on first render
 */
const PhoneForm: FC<PhoneFormProps> = ({
  onChange,
  isValid,
  defaultValue,
  autoFocus,
  label,
  ...textFieldProps
}) => {
  const formatPhoneNumber = (input: string): string => {
    const digits = input.replace(/\D/g, "").substring(0, 10);
    const len = digits.length;

    if (len < 4) return digits;
    if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const [valid, setValid] = useState(true);
  const [lastValue, setLastValue] = useState("");
  const [value, setValue] = useState(() =>
    defaultValue ? formatPhoneNumber(defaultValue) : "",
  );

  const validateAndSubmit = (formattedValue: string) => {
    const digits = formattedValue.replace(/\D/g, "");
    const isValidPhone = /^\d{10}$/.test(digits);
    if (isValidPhone) {
      if (onChange && lastValue !== digits) onChange(digits);
      setValid(true);
      if (isValid) isValid(true);
      setLastValue(digits);
    } else {
      if (onChange && lastValue !== digits) onChange(undefined);
      setValid(false);
      if (isValid) isValid(false);
      setLastValue(digits);
    }
  };

  return (
    <TextField
      fullWidth
      id="phone-number-field"
      autoFocus={autoFocus}
      color={valid ? "primary" : "error"}
      type="tel"
      value={value}
      label={label || "Enter Phone"}
      onChange={(e) => {
        const formatted = formatPhoneNumber(e.target.value);
        setValue(formatted);
        validateAndSubmit(formatted);
      }}
      inputProps={{
        inputMode: "numeric",
        pattern: "[0-9]*",
        style: {
          MozAppearance: "textfield",
        },
      }}
      sx={{
        "& input[type=tel]::-webkit-outer-spin-button": {
          WebkitAppearance: "none",
          margin: 0,
        },
        "& input[type=tel]::-webkit-inner-spin-button": {
          WebkitAppearance: "none",
          margin: 0,
        },
      }}
      {...textFieldProps}
    />
  );
};

export default PhoneForm;
